/**
 * One feature's shape over the range, at row height.
 *
 * ── Why a sparkline and not a number ─────────────────────────────────────────────────────────
 * "Used 180 times" is three different findings wearing one coat: a launch spike that stopped, a
 * steady habit, and something still climbing. They call for opposite responses — leave it, coach
 * it, or go and explain it — and only the shape tells them apart. It sits beside the count rather
 * than replacing it, because the shape answers "is it growing" and the count answers "how much".
 *
 * ── What it deliberately does not have ───────────────────────────────────────────────────────
 * No axis, no gridlines, no labels. At this size every one of those costs more than it says, and
 * the row already carries the total, the people and the last-used date as text. Nothing here is
 * reachable ONLY by looking at the line — which is the rule that lets a chart this small be
 * honest rather than decorative.
 *
 * Scaled to its OWN maximum, on purpose: these rows are compared by the numbers beside them, and
 * a shared scale would flatten every quiet feature into a straight line at zero, which reads as
 * "never used" rather than "used less than the loudest one".
 */
import { SERIES_PRIMARY } from './chartTokens';

const WIDTH = 72;
const HEIGHT = 20;
/** Keeps the 2px stroke inside the box at the extremes rather than clipping it. */
const PAD = 2;

export function Sparkline({ values, label }: { values: number[]; label: string }) {
  if (values.length === 0) return null;

  const max = Math.max(...values, 0);
  const span = Math.max(values.length - 1, 1);
  const x = (at: number) => PAD + (at / span) * (WIDTH - PAD * 2);
  const y = (value: number) =>
    max === 0 ? HEIGHT - PAD : HEIGHT - PAD - (value / max) * (HEIGHT - PAD * 2);

  const line = values.map((value, at) => `${at === 0 ? 'M' : 'L'}${x(at)},${y(value)}`).join(' ');
  const area = `${line} L${x(values.length - 1)},${HEIGHT} L${x(0)},${HEIGHT} Z`;
  const lastAt = values.length - 1;

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={label}
      className="shrink-0 overflow-visible"
    >
      <path d={area} fill={SERIES_PRIMARY} opacity={0.1} />
      <path
        d={line}
        fill="none"
        stroke={SERIES_PRIMARY}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* The latest day gets a marker: on a line this short, "where does it end" is the whole
          question and the eye should not have to find the right-hand edge. */}
      {max > 0 && (
        <circle cx={x(lastAt)} cy={y(values[lastAt])} r={2} fill={SERIES_PRIMARY} />
      )}
    </svg>
  );
}

/**
 * Second half against first — the plainest form of "is this catching on".
 *
 * Returns null rather than a number when the range is too short to halve or when BOTH halves are
 * empty: a percentage computed from nothing is the most confident-looking wrong answer available,
 * and this board is read two weeks after a launch when every series is small.
 */
export function halfOverHalf(values: number[]): { change: number; first: number; second: number } | null {
  if (values.length < 4) return null;
  const mid = Math.floor(values.length / 2);
  const first = values.slice(0, mid).reduce((sum, n) => sum + n, 0);
  const second = values.slice(mid).reduce((sum, n) => sum + n, 0);
  if (first === 0 && second === 0) return null;
  // From a zero first half there is no ratio to state — "new in the second half" is the finding,
  // and Infinity% is not a way to say it.
  if (first === 0) return { change: Number.POSITIVE_INFINITY, first, second };
  return { change: (second - first) / first, first, second };
}
